/**
 * @fileoverview Hook de UI Layer para estado de interfaz de usuario de tags de clientes en CustomerDetailTagsMC
 * @module useCustomerDetailTagsUI
 * @category UI Layer
 * @since 1.0.0
 * @version 3.1.0
 */

import {
  useState,
  useCallback,
  useMemo,
  useEffect
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

/**
 * Hook de UI Layer para estado de interfaz de usuario de tags de clientes en CustomerDetailTagsMC.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades,
 * enfocándose en el estado de presentación, configuraciones visuales, handlers de eventos
 * de interfaz y gestión de traducciones para el componente CustomerDetailTagsMC.
 */
const useCustomerDetailTagsUI = (initialTags = [], localCreatedTags = [], loading = false) => {
  const { t } = useTranslation('InboxMC');
  const { agent, allow_write_tags } = useSelector((state) => state.mainReducer?.currentRetailerUser);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const disableWriteForAgent = useMemo(() => agent && !allow_write_tags, [agent, allow_write_tags]);

  /**
   * Efecto para inicializar tags seleccionados cuando cambian los tags iniciales.
   */
  useEffect(() => {
    const initialTagIds = initialTags.map((tag) => tag.id);
    setSelectedTags(initialTagIds);
  }, [initialTags]);

  /**
   * Detección memoizada de cambios pendientes comparando estado inicial vs actual.
   */
  const hasChanges = useMemo(() => {
    const originalTagIds = initialTags.map((tag) => tag.id);
    const currentTagIds = selectedTags.filter((id) => !String(id).startsWith('temp_'));

    // Check for new local tags or selection changes
    return localCreatedTags.length > 0
      || originalTagIds.length !== currentTagIds.length
      || !originalTagIds.every((id) => currentTagIds.includes(id));
  }, [initialTags, selectedTags, localCreatedTags]);

  /**
   * Handler memoizado para cambios en el componente multi-select.
   */
  const handleMultiSelectChange = useCallback((_, selectedOptions) => {
    setSelectedTags(selectedOptions || []);
  }, []);

  /**
   * Handler memoizado para toggle del modo de edición con callback de guardado.
   */
  const handleEditModeToggle = useCallback((onSave) => {
    if (isEditing) {
      // If editing, trigger save action
      onSave?.();
    } else {
      // If not editing, enter edit mode
      setIsEditing(true);
    }
  }, [isEditing]);

  /**
   * Handler memoizado para salir del modo de edición.
   */
  const exitEditMode = useCallback(() => {
    setIsEditing(false);
  }, []);

  /**
   * Ícono memoizado del botón basado en estado del componente.
   */
  const buttonIcon = useMemo(() => {
    if (isEditing && loading) return 'sync-outline';
    if (isEditing) return 'checkmark-outline';
    return 'edit-outline';
  }, [isEditing, loading]);

  /**
   * Configuración memoizada del botón de editar/guardar.
   */
  const editButtonConfig = useMemo(() => ({
    outlined: true,
    size: 'xs',
    variant: 'SECONDARY',
    icon: buttonIcon,
    className: 'tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 !tw-p-0',
    iconClassName: 'tw-text-gray-600 !tw-w-4 !tw-h-4 tw-flex-shrink-0',
    disabled: loading
  }), [buttonIcon, loading]);

  /**
   * Configuración memoizada del botón de guardado con traducciones.
   */
  const saveButtonConfig = useMemo(() => ({
    text: t('customerDetails.save'),
    className: 'tw-flex-1',
    disabled: loading
  }), [t, loading]);

  /**
   * ID memoizado del tooltip para mejor performance y referencias estables.
   */
  const tooltipId = useMemo(() => 'customer-tags-edit-tooltip', []);

  /**
   * Texto memoizado del tooltip basado en estado actual.
   */
  const tooltipText = useMemo(() => {
    if (loading) return t('customerDetails.saving');
    return isEditing
      ? t('customerDetails.save')
      : t('customerDetails.editButtonTooltip');
  }, [isEditing, loading, t]);

  /**
   * Configuración memoizada del componente multi-select.
   */
  const multiSelectConfig = useMemo(() => ({
    id: 'customer-tags-selector',
    name: 'customerTags',
    search: true,
    placeholder: t('customerDetails.tags.select'),
    allowCreate: !disableWriteForAgent,
    createOptionText: t('customerDetails.tags.create', { defaultValue: 'Crear' })
  }), [t, disableWriteForAgent]);

  /**
   * Texto memoizado del estado de cambios para feedback visual.
   */
  const changesStatusText = useMemo(() => {
    if (!hasChanges) return null;

    return localCreatedTags.length > 0
      ? `(${localCreatedTags.length} ${t('customerDetails.tags.pendingCreation', { defaultValue: 'pendientes' })})`
      : `(${t('customerDetails.tags.hasChanges', { defaultValue: 'modificado' })})`;
  }, [hasChanges, localCreatedTags.length, t]);

  /**
   * Objeto de retorno memoizado con API completa de UI Layer.
   */
  return useMemo(() => ({
    // UI State
    isEditing,
    selectedTags,
    hasChanges,
    loading,

    // Handlers
    handleMultiSelectChange,
    handleEditModeToggle,
    exitEditMode,
    setSelectedTags,

    // UI Configurations
    editButtonConfig,
    saveButtonConfig,
    multiSelectConfig,
    tooltipId,
    tooltipText,
    changesStatusText,

    // Translations
    tagsTitle: t('customerDetails.tags.title')
  }), [
    isEditing,
    selectedTags,
    hasChanges,
    loading,
    handleMultiSelectChange,
    handleEditModeToggle,
    exitEditMode,
    setSelectedTags,
    editButtonConfig,
    saveButtonConfig,
    multiSelectConfig,
    tooltipId,
    tooltipText,
    changesStatusText,
    t
  ]);
};

export default useCustomerDetailTagsUI;
